/*  TEST LOGIN ACCES */

/* ROLES from loggedin user privilege level */
export const ROLES = {
  SUPER_MEGA_ADMIN: 4,
  SUPER_ADMIN: 3,
  ADMIN: 2,
  CUSTOMER: 1,
  EMPLOYEE: 0,
  LOGOUT: -1
};

/* Named access levels for projects that gate on an explicit minimum (structurewood) */
export const LEVELS = {
  OPERATOR: 1,
  SHIFT_LEADER: 2,
  ADMINISTRATOR: 3
};

/* Generic threshold gate: true when the field/button should be disabled for this privilege.
   Opt-in via `minPrivilege: N` on a field or button config. Configs without it are unaffected. */
export function isBelowMinPrivilege(item, privilege) {
  const min = item?.minPrivilege;
  if (min == null) return false;
  return (privilege ?? -1) < min;
}

export const PERMISSIONS = {
  CAN_ACCES_ROUTE: 1000,
  CAN_EDIT_ORDER: 1001,
  CAN_EDIT_JDQUEUE: 1002,
  CAN_ACCESS_SIDEBAR: 1003,
  CAN_MODIFY_USERS: 1004,
  CAN_MODIFY_GLOBAL_SETTINGS: 1005,
  CAN_ACCESS_ARCHIVE_MODE: 1006,
  CAN_ACCESS_HEADER: 1007,
  CAN_ACCESS_PLC_VNC: 1008,
  CAN_CHERRYPICK: 1009,
  CAN_VIEW_COMM: 1010,
  CAN_SEND_NOTIFICATION: 1011,
  CAN_LOAD_LINESTATUS: 1012,
  CAN_CHANGE_UNIT_SYSTEM: 1012,
  CAN_ACCESS_DEBUG_MODE: 1013,
  CAN_ACCESS_SUPER_MEGA_ADMIN: 1014,
  CAN_EXPORT_CAMERA_VIDEO: 1015,
  CAN_ACCESS_LINE_EFFICIENCY_STATS: 1016
};

export const getPermissions = {
  [ROLES.SUPER_MEGA_ADMIN]: [
    PERMISSIONS.CAN_EDIT_JDQUEUE,
    PERMISSIONS.CAN_ACCES_ROUTE,
    PERMISSIONS.CAN_ACCESS_ARCHIVE_MODE,
    PERMISSIONS.CAN_EDIT_ORDER,
    PERMISSIONS.CAN_VIEW_COMM,
    PERMISSIONS.CAN_ACCESS_SIDEBAR,
    PERMISSIONS.CAN_CHERRYPICK,
    PERMISSIONS.CAN_ACCESS_PLC_VNC,
    PERMISSIONS.CAN_ACCESS_HEADER,
    PERMISSIONS.CAN_MODIFY_GLOBAL_SETTINGS,
    PERMISSIONS.CAN_SEND_NOTIFICATION,
    PERMISSIONS.CAN_LOAD_LINESTATUS,
    PERMISSIONS.CAN_CHANGE_UNIT_SYSTEM,
    PERMISSIONS.CAN_ACCESS_DEBUG_MODE,
    PERMISSIONS.CAN_ACCESS_SUPER_MEGA_ADMIN,
    PERMISSIONS.CAN_EXPORT_CAMERA_VIDEO,
    PERMISSIONS.CAN_ACCESS_LINE_EFFICIENCY_STATS
  ],
  [ROLES.SUPER_ADMIN]: [
    PERMISSIONS.CAN_EDIT_JDQUEUE,
    PERMISSIONS.CAN_ACCES_ROUTE,
    PERMISSIONS.CAN_ACCESS_ARCHIVE_MODE,
    PERMISSIONS.CAN_EDIT_ORDER,
    PERMISSIONS.CAN_VIEW_COMM,
    PERMISSIONS.CAN_ACCESS_SIDEBAR,
    PERMISSIONS.CAN_CHERRYPICK,
    PERMISSIONS.CAN_ACCESS_PLC_VNC,
    PERMISSIONS.CAN_ACCESS_HEADER,
    PERMISSIONS.CAN_MODIFY_GLOBAL_SETTINGS,
    PERMISSIONS.CAN_SEND_NOTIFICATION,
    PERMISSIONS.CAN_LOAD_LINESTATUS,
    PERMISSIONS.CAN_CHANGE_UNIT_SYSTEM,
    PERMISSIONS.CAN_ACCESS_DEBUG_MODE,
    PERMISSIONS.CAN_EXPORT_CAMERA_VIDEO,
    PERMISSIONS.CAN_ACCESS_LINE_EFFICIENCY_STATS
  ],
  [ROLES.ADMIN]: [
    PERMISSIONS.CAN_EDIT_ORDER,
    PERMISSIONS.CAN_ACCESS_ARCHIVE_MODE,
    PERMISSIONS.CAN_ACCES_ROUTE,
    PERMISSIONS.CAN_ACCESS_SIDEBAR,
    PERMISSIONS.CAN_CHERRYPICK,
    PERMISSIONS.CAN_ACCESS_PLC_VNC,
    PERMISSIONS.CAN_ACCESS_HEADER,
    PERMISSIONS.CAN_MODIFY_GLOBAL_SETTINGS,
    PERMISSIONS.CAN_CHANGE_UNIT_SYSTEM
  ],
  [ROLES.CUSTOMER]: [PERMISSIONS.CAN_MODIFY_GLOBAL_SETTINGS],
  [ROLES.EMPLOYEE]: [PERMISSIONS.CAN_MODIFY_GLOBAL_SETTINGS, PERMISSIONS.CAN_MODIFY_USERS],
  [ROLES.LOGOUT]: []
};

export const PROJECT_PERMISSION_OVERRIDES = {
  structurewood: {
    [ROLES.CUSTOMER]: [PERMISSIONS.CAN_ACCES_ROUTE, PERMISSIONS.CAN_ACCESS_SIDEBAR, PERMISSIONS.CAN_ACCESS_HEADER],
    [ROLES.ADMIN]: [
      PERMISSIONS.CAN_EDIT_ORDER,
      PERMISSIONS.CAN_ACCESS_ARCHIVE_MODE,
      PERMISSIONS.CAN_ACCES_ROUTE,
      PERMISSIONS.CAN_ACCESS_SIDEBAR,
      PERMISSIONS.CAN_CHERRYPICK,
      PERMISSIONS.CAN_ACCESS_PLC_VNC,
      PERMISSIONS.CAN_ACCESS_HEADER,
      PERMISSIONS.CAN_CHANGE_UNIT_SYSTEM
    ]
  },
  fournier: {
    1: [PERMISSIONS.CAN_ACCES_ROUTE, PERMISSIONS.CAN_ACCESS_SIDEBAR, PERMISSIONS.CAN_ACCESS_HEADER]
  }
};

export function getPermissionsForRole(roleKey, projectName = process.env.REACT_APP_PROJECT_NAME) {
  const override = PROJECT_PERMISSION_OVERRIDES[projectName]?.[roleKey];
  return override ?? getPermissions[roleKey];
}

/* Minimum privilege allowed to save an edit form, per project.
   Structurewood level 1 is read-only, so it gets Cancel but no Confirm.
   Projects not listed here can always save, as before. */
export const PROJECT_MIN_PRIVILEGE_TO_SAVE = {
  structurewood: LEVELS.SHIFT_LEADER
};

export const PROJECT_MIN_PRIVILEGE_TO_SEND_WS = {
  fournier: 2
};

export function canSaveForms(privilege, projectName = process.env.REACT_APP_PROJECT_NAME) {
  const min = PROJECT_MIN_PRIVILEGE_TO_SAVE[projectName];
  if (min == null) return true;
  return (privilege ?? -1) >= min;
}

const READ_ONLY_WS_TYPES = ['client-ping', 'request-clt-status-snapshot'];

export function canSendWebsocketMessage(privilege, wsType, projectName = process.env.REACT_APP_PROJECT_NAME) {
  if (READ_ONLY_WS_TYPES.includes(wsType)) return true; // lahko senda dolocene :3

  const min = PROJECT_MIN_PRIVILEGE_TO_SEND_WS[projectName];
  return min == null || (privilege ?? -1) >= min;
}
